import { useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import { Box, Tooltip, styled } from '@mui/material';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { CompareArrows } from '@mui/icons-material';
import { createInvestmentEditRoute } from 'adminConstants/routes';
import { format } from 'date-fns';
import { round } from 'lodash';
import NewSharesAllocatedCell from './NewSharesAllocatedCell';
import ExitSharesSoldCell from './ExitSharesSoldCell';
import UploadShareConsiderationSheet from './UploadShareConsiderationSheet';
import {
  ConsiderationType,
  ShareConsiderationAllocationMethod,
} from 'further-types/exit';
import { ExitDetailsFormValues } from '../ExitDetailsForm';
import type { ShareConsideration } from '../types';

const TrancheCell = styled('span')(() => ({
  display: 'inline-block',
  minWidth: '7rem',
}));

const TooltipLink = styled('a')(({ theme }) => ({
  color: theme.palette.text.rootColor,
}));

const SummaryRow = styled('tr')(() => ({
  backgroundColor: '#FBFBFA',
  marginTop: 4,
}));

const SummaryCell = styled('td')(() => ({
  fontWeight: 'bold',
  padding: '12px 8px',
}));

type ShareConsiderationItem = {
  shareholdingId: string;
  tranche?: { fundName?: string };
  sourceInvestmentId?: string;
  investor?: { fullName?: string };
  transferSourceInvestor?: { fullName?: string };
  transferDate?: string;
  investmentDate?: string;
  allocationDate?: string;
  sharesAllocatedForSale?: number;
  suggestedSharesAllocatedForSale?: number;
  noOfShares?: number;
  newSharesAllocated?: number;
  suggestedNewSharesAllocated?: number;
  shareConsiderationValuePerShare?: number;
  externalInvestmentId?: string | null;
};

type StagedShareConsiderationData = {
  shareholdingId: string;
  allocationDate?: string;
  shareClass: string;
  shareAllocationUploadId?: string;
  investorName?: string;
  tranche?: { _id: string; fundName?: string };
  noOfShares: number | string;
  sharesAllocatedForSale: number | null | undefined;
  sharePrice: number;
  accruedFeesCharged: number;
  externalInvestmentId?: string | null;
  transferDate?: string;
  sourceInvestmentId?: string;
  transferSourceInvestor?: { fullName: string };
  shareAllocationUploadLogId?: string;
  shareAllocationRoundingDiff?: number;
  totalSaleValue?: number;
  performanceFee: number;
  newSharesAllocated?: number;
  investmentDate?: string;
  shareConsiderationValuePerShare?: number;
  investor?: {
    fullName: string;
  };
};

type Props = {
  exitDetails: ExitDetailsFormValues;
  shareConsiderations: Array<ShareConsideration>;
  setShareConsiderations: React.Dispatch<
    React.SetStateAction<Array<ShareConsideration>>
  >;
  disableTable: boolean;
  isBeingEdited: boolean;
  exitId?: string;
  onUploadComplete?: () => void;
  isBeingCorrected?: boolean;
  maintainOriginalExitDetails?: boolean;
};

const transformStagedDataToShareConsideration = (
  data: Array<StagedShareConsiderationData | null>,
  allocationMethod: ShareConsiderationAllocationMethod,
): Array<ShareConsideration> => {
  return data
    .filter(
      (item): item is StagedShareConsiderationData =>
        item !== null &&
        typeof item === 'object' &&
        'shareholdingId' in item &&
        'shareClass' in item,
    )
    .map((sc) => {
      // If upload method is selected, set all newSharesAllocated to 0
      const newSharesAllocated =
        allocationMethod === ShareConsiderationAllocationMethod.Upload
          ? 0
          : sc.newSharesAllocated ?? 0;

      return {
        shareholdingId: sc.shareholdingId,
        shareClass: sc.shareClass,
        noOfShares:
          typeof sc.noOfShares === 'string'
            ? Number(sc.noOfShares)
            : sc.noOfShares ?? 0,
        sharesAllocatedForSale: sc.sharesAllocatedForSale ?? 0,
        sharePrice: sc.sharePrice,
        accruedFeesCharged: sc.accruedFeesCharged ?? 0,
        performanceFee: sc.performanceFee ?? 0,
        allocationDate: sc.allocationDate,
        shareAllocationUploadId: sc.shareAllocationUploadId,
        investorName: sc.investorName ?? '',
        tranche: sc.tranche,
        externalInvestmentId: sc.externalInvestmentId ?? null,
        transferDate: sc.transferDate,
        sourceInvestmentId: sc.sourceInvestmentId,
        transferSourceInvestor: sc.transferSourceInvestor,
        shareAllocationUploadLogId: sc.shareAllocationUploadLogId,
        shareAllocationRoundingDiff: sc.shareAllocationRoundingDiff,
        totalSaleValue: sc.totalSaleValue,
        newSharesAllocated,
        investmentDate: sc.investmentDate,
        shareConsiderationValuePerShare:
          sc.shareConsiderationValuePerShare ?? 0,
        investor: sc.investor,
        // store the original newSharesAllocated so we know
        // if the user has changed it via the input box
        suggestedNewSharesAllocated: newSharesAllocated,
        // store the original sharesAllocatedForSale so we know
        // if the user has changed it via the input box
        suggestedSharesAllocatedForSale: sc.sharesAllocatedForSale ?? 0,
      };
    });
};

const ReviewShareConsiderations: React.FC<Props> = ({
  exitDetails,
  shareConsiderations,
  setShareConsiderations,
  disableTable,
  isBeingEdited,
  exitId,
  onUploadComplete,
  isBeingCorrected,
  maintainOriginalExitDetails,
}) => {
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  const mutationOptions = useHandleTrpcMutation()({});
  const stageForEditMutation =
    api.exit.stageForEdit.useMutation(mutationOptions);
  const stageForCreationMutation =
    api.exit.stageForCreation.useMutation(mutationOptions);
  const stageForCorrectionMutation =
    api.exit.stageForCorrection.useMutation(mutationOptions);

  // Fetch share consideration history when editing
  useEffect(() => {
    // Update shareConsiderations when stageForEditMutation.data changes
    if (stageForEditMutation.data && isBeingEdited) {
      const formattedInitialData = transformStagedDataToShareConsideration(
        stageForEditMutation.data,
        exitDetails.shareConsiderationAllocationMethod ??
          ShareConsiderationAllocationMethod.Automatic,
      );
      setInitialShareConsiderations(formattedInitialData);
    }
  }, [
    stageForEditMutation.data,
    isBeingEdited,
    exitDetails.shareConsiderationAllocationMethod,
  ]);

  useEffect(() => {
    // Update shareConsiderations when stageForCreationMutation.data changes
    // Always update when stageForCreationMutation.data is available and we're not editing
    if (stageForCreationMutation.data && !isBeingEdited) {
      const formattedInitialData = stageForCreationMutation.data?.map(
        (sc: ShareConsideration) => {
          // If upload method is selected, set all newSharesAllocated to 0
          const newSharesAllocated =
            exitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Upload
              ? 0
              : sc.newSharesAllocated ?? 0;

          return {
            ...sc,
            newSharesAllocated,
            // store the original newSharesAllocated so we know
            // if the user has changed it via the input box
            suggestedNewSharesAllocated: newSharesAllocated,
            // store the original sharesAllocatedForSale so we know
            // if the user has changed it via the input box
            suggestedSharesAllocatedForSale:
              sc.suggestedSharesAllocatedForSale ??
              sc.sharesAllocatedForSale ??
              0,
          };
        },
      );
      setInitialShareConsiderations(formattedInitialData);
    }
  }, [
    stageForCreationMutation.data,
    isBeingEdited,
    exitDetails.shareConsiderationAllocationMethod,
  ]);

  useEffect(() => {
    // Update shareConsiderations when stageForCorrectionMutation.data changes
    if (stageForCorrectionMutation.data && isBeingCorrected) {
      const formattedInitialData = stageForCorrectionMutation.data?.map(
        (sc: ShareConsideration) => {
          // If upload method is selected, set all newSharesAllocated to 0
          const newSharesAllocated =
            exitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Upload
              ? 0
              : sc.newSharesAllocated ?? 0;

          return {
            ...sc,
            newSharesAllocated,
            suggestedNewSharesAllocated: newSharesAllocated,
            suggestedSharesAllocatedForSale:
              sc.suggestedSharesAllocatedForSale ??
              sc.sharesAllocatedForSale ??
              0,
          };
        },
      );
      setInitialShareConsiderations(formattedInitialData);
    }
  }, [
    stageForCorrectionMutation.data,
    isBeingCorrected,
    exitDetails.shareConsiderationAllocationMethod,
  ]);

  useEffect(() => {
    // fetch the investors with shareholdings and their share consideration allocations
    if (exitDetails && Object.keys(exitDetails).length) {
      if (isBeingEdited && exitId) {
        const params = {
          exitId,
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          firmId: exitDetails.firmId,
          sharePrice: Number(exitDetails.sharePrice),
          taxableValueSharePrice: Number(exitDetails.taxableValueSharePrice),
          isReceivedValueSharePriceUnchanged: true, // This will need to be compared with previousExitDetails if available
          considerationType: exitDetails.considerationType,
          shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId,
          totalConsiderationShares: exitDetails.totalConsiderationShares
            ? Number(exitDetails.totalConsiderationShares)
            : undefined,
          shareConsiderationValuePerShare:
            exitDetails.shareConsiderationValuePerShare
              ? Number(exitDetails.shareConsiderationValuePerShare)
              : undefined,
          shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
            ? Number(exitDetails.shareConsiderationValueTotal)
            : undefined,
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            exitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            exitDetails.permitPartialShareAllocation,
          maintainOriginalExitDetails: isBeingCorrected
            ? maintainOriginalExitDetails ?? false
            : false,
        };
        stageForEditMutation.mutate(params);
      } else if (isBeingCorrected && exitId) {
        stageForCorrectionMutation.mutate({
          exitId,
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses,
          firmId: exitDetails.firmId,
          sharePrice: Number(exitDetails.sharePrice),
          taxableValueSharePrice: Number(exitDetails.taxableValueSharePrice),
          sharesToBeSold: exitDetails.sharesToBeSold,
          shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
          considerationType:
            exitDetails.considerationType ?? ConsiderationType.Cash,
          shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId,
          totalConsiderationShares: exitDetails.totalConsiderationShares
            ? Number(exitDetails.totalConsiderationShares)
            : undefined,
          shareConsiderationValuePerShare:
            exitDetails.shareConsiderationValuePerShare
              ? Number(exitDetails.shareConsiderationValuePerShare)
              : undefined,
          shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
            ? Number(exitDetails.shareConsiderationValueTotal)
            : undefined,
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            exitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            exitDetails.permitPartialShareAllocation,
          maintainOriginalExitDetails: maintainOriginalExitDetails ?? false,
        });
      } else {
        stageForCreationMutation.mutate({
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses,
          firmId: exitDetails.firmId,
          sharePrice: Number(exitDetails.sharePrice),
          taxableValueSharePrice: Number(exitDetails.taxableValueSharePrice),
          sharesToBeSold: exitDetails.sharesToBeSold,
          shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
          considerationType:
            exitDetails.considerationType ?? ConsiderationType.Cash,
          shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId,
          totalConsiderationShares: exitDetails.totalConsiderationShares
            ? Number(exitDetails.totalConsiderationShares)
            : undefined,
          shareConsiderationValuePerShare:
            exitDetails.shareConsiderationValuePerShare
              ? Number(exitDetails.shareConsiderationValuePerShare)
              : undefined,
          shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
            ? Number(exitDetails.shareConsiderationValueTotal)
            : undefined,
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            exitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            exitDetails.permitPartialShareAllocation,
          maintainOriginalExitDetails: isBeingCorrected
            ? maintainOriginalExitDetails ?? false
            : false,
        });
      }
    }
  }, [
    exitDetails,
    isBeingEdited,
    exitId,
    isBeingCorrected,
    maintainOriginalExitDetails,
  ]);

  const totals = useMemo(
    () =>
      shareConsiderations.reduce(
        (acc, sc) => {
          const sharesAllocatedForSale = sc.sharesAllocatedForSale ?? 0;
          const newSharesAllocated = sc.newSharesAllocated ?? 0;

          const totalSharesAllocatedForSale = Number(
            (acc.totalSharesAllocatedForSale + sharesAllocatedForSale).toFixed(
              10,
            ),
          );
          const totalNewSharesAllocated = Number(
            (acc.totalNewSharesAllocated + newSharesAllocated).toFixed(10),
          );

          return {
            totalSharesAllocatedForSale,
            totalNewSharesAllocated,
          };
        },
        { totalSharesAllocatedForSale: 0, totalNewSharesAllocated: 0 },
      ),
    [shareConsiderations],
  );

  const setInitialShareConsiderations = (input: Array<ShareConsideration>) => {
    setShareConsiderations(input);
  };

  const setOverride = useCallback(
    (
      shareholdingId: string,
      overrideProperties: Partial<ShareConsideration>,
    ) => {
      setShareConsiderations((prevState: Array<ShareConsideration>) => {
        return prevState.map((sc: ShareConsideration): ShareConsideration => {
          if (sc.shareholdingId === shareholdingId) {
            return {
              ...sc,
              ...overrideProperties,
            };
          }
          return sc;
        });
      });
    },
    [setShareConsiderations],
  );

  const columns = useMemo(
    () => [
      {
        label: 'Tranche',
        key: 'tranche',
        sort: false,
        render: (elm: ShareConsiderationItem) => (
          <TrancheCell>{elm?.tranche?.fundName ?? ''}</TrancheCell>
        ),
      },
      {
        label: 'Name',
        key: 'name',
        sort: false,
        render: (elm: ShareConsiderationItem) => {
          if (!elm.sourceInvestmentId) {
            return elm?.investor?.fullName ?? '';
          }

          const sourceInvestorName = elm?.transferSourceInvestor?.fullName;
          return (
            <Tooltip
              title={
                <div>
                  This investor is the recipient of a transfer from{' '}
                  <TooltipLink
                    href={createInvestmentEditRoute(elm?.sourceInvestmentId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {sourceInvestorName}
                  </TooltipLink>{' '}
                  on {dateToLabel(elm?.transferDate)}.
                </div>
              }
            >
              <Box alignItems="center" display="flex" gap={4}>
                {elm?.investor?.fullName ?? ''}
                <CompareArrows />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        label: 'Subscription date',
        key: 'subscriptionDate',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          elm.investmentDate ? dateToLabel(elm.investmentDate) : '-',
      },
      {
        label: 'Allocation date',
        key: 'allocationDate',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          elm.allocationDate ? dateToLabel(elm.allocationDate) : '-',
      },
      {
        label: 'Shares held',
        key: 'noOfShares',
        sort: false,
        render: (elm: ShareConsiderationItem) => {
          const shareConsideration = shareConsiderations.find(
            (sc) => sc.shareholdingId === elm.shareholdingId,
          );
          return numberToDisplayString(
            shareConsideration?.noOfShares ?? elm.noOfShares ?? 0,
            {
              maximumFractionDigits: 10,
            },
          );
        },
      },
      {
        label: 'Exit shares sold',
        key: 'sharesAllocatedForSale',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          isBeingEdited
            ? numberToDisplayString(elm.sharesAllocatedForSale ?? 0, {
                maximumFractionDigits: 10,
              })
            : (() => {
                const shareConsideration = shareConsiderations.find(
                  (sc) => sc.shareholdingId === elm.shareholdingId,
                );

                return (
                  <ExitSharesSoldCell
                    cell={{
                      shareholdingId: elm.shareholdingId,
                      sharesAllocatedForSale: elm.sharesAllocatedForSale ?? 0,
                      suggestedSharesAllocatedForSale:
                        elm.suggestedSharesAllocatedForSale ??
                        elm.sharesAllocatedForSale ??
                        0,
                      noOfShares:
                        shareConsideration?.noOfShares ?? elm.noOfShares ?? 0,
                    }}
                    setOverride={setOverride}
                    disableTable={disableTable}
                  />
                );
              })(),
      },
      {
        label: 'New shares allocated',
        key: 'newSharesAllocated',
        sort: false,
        render: (elm: ShareConsiderationItem) => (
          <NewSharesAllocatedCell
            cell={{
              shareholdingId: elm.shareholdingId,
              newSharesAllocated: elm.newSharesAllocated ?? 0,
              suggestedNewSharesAllocated: elm.suggestedNewSharesAllocated ?? 0,
            }}
            setOverride={setOverride}
            disableTable={disableTable}
          />
        ),
      },
      {
        label: 'Share value',
        key: 'shareValue',
        sort: false,
        render: (elm: ShareConsiderationItem) => {
          if (!elm.newSharesAllocated || elm.newSharesAllocated === 0) {
            return '-';
          }
          const totalValue = round(
            (elm.newSharesAllocated ?? 0) *
              (elm.shareConsiderationValuePerShare ?? 0),
            2,
          );
          return numberToCurrencyString(totalValue, {
            fallback: '-',
          });
        },
      },
      {
        label: 'External subscription ID',
        key: 'externalInvestmentId',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          elm.externalInvestmentId ?? '-',
      },
    ],
    [setOverride, disableTable, isBeingEdited, shareConsiderations],
  );

  const isUploadMethod =
    exitDetails.shareConsiderationAllocationMethod ===
    ShareConsiderationAllocationMethod.Upload;

  const handleUploadComplete = () => {
    setIsUploadComplete(true);
    onUploadComplete?.();
  };

  // Reset upload complete state when allocation method changes away from upload
  useEffect(() => {
    if (
      exitDetails.shareConsiderationAllocationMethod !==
      ShareConsiderationAllocationMethod.Upload
    ) {
      setIsUploadComplete(false);
    }
  }, [exitDetails.shareConsiderationAllocationMethod]);

  // Show upload component if upload method is selected, we have data, and upload not complete
  // BUT skip upload for corrections - go straight to the table with prepopulated data
  if (
    isUploadMethod &&
    shareConsiderations.length &&
    !isUploadComplete &&
    !isBeingCorrected
  ) {
    return (
      <UploadShareConsiderationSheet
        exitDetails={exitDetails}
        shareConsiderations={shareConsiderations}
        setShareConsiderations={setShareConsiderations}
        onTemplateReset={() => {
          // Reset upload state when template is reset
          setIsUploadComplete(false);
        }}
        onUploadComplete={handleUploadComplete}
      />
    );
  }

  // Show table for the current/new share consideration
  const currentMutationData = isBeingEdited
    ? stageForEditMutation.data
    : isBeingCorrected
    ? stageForCorrectionMutation.data
    : stageForCreationMutation.data;

  const isLoadingMutationData =
    !!currentMutationData && !shareConsiderations.length;

  return (
    <>
      <Table
        columns={columns}
        tablebody={shareConsiderations}
        loading={
          !!(
            stageForCreationMutation.isPending ||
            stageForEditMutation.isPending ||
            stageForCorrectionMutation.isPending ||
            isLoadingMutationData
          )
        }
        emptyMessage="No share considerations found for this exit."
        variant="nohover"
        TableFooter={
          <tfoot>
            <SummaryRow>
              <SummaryCell align="right" colSpan={5}></SummaryCell>
              <SummaryCell align="right">
                {numberToDisplayString(totals.totalSharesAllocatedForSale, {
                  maximumFractionDigits: 10,
                })}
              </SummaryCell>
              <SummaryCell align="right" colSpan={2}>
                {numberToDisplayString(totals.totalNewSharesAllocated, {
                  maximumFractionDigits: 2,
                })}
              </SummaryCell>
              <SummaryCell align="right" colSpan={1}></SummaryCell>
            </SummaryRow>
          </tfoot>
        }
      />
    </>
  );
};

export default ReviewShareConsiderations;
